import React from 'react';
import PropTypes from 'prop-types';
import { getCompoundSentenceList, getListItems } from '@format/list';
import { LinkButton } from '@ui/button';
import { costFormatter } from '@format/currency';
import { HALF_PORTION, FREQUENCIES } from '../../products/constants';
import { getPlanFreeItems } from '../PricingList/functions';
import { SummaryItem } from './SummaryItem';
import {
  StyledConfirmationOrderSummary,
  OrderSummaryTitle,
  Section,
  SectionTitle,
  SectionSubtitle,
  ButtonWrapper,
} from './styles.js';

export const SlimConfirmationOrderSummary = ({
  orders,
  orderTotalCost,
  orderSubtotal,
  orderTotalTax,
  orderSummaryTitle = 'Order Summary',
  plans,
  pricingList,
  freeItems,
  onClickAccount,
}) => {
  const starterBoxes = [];

  for (let [index, order] of orders.entries()) {
    const {
      petName,
      breeds,
      price,
      discountedPrice,
      recipes,
      addons,
      portionSize,
      weeksOfFood,
    } = order;

    const pupSummaryTitle = `${FREQUENCIES[weeksOfFood]}-week Starter Box`;

    const { fresh, dry } = recipes;
    const recipeFilters = {
      fresh: { label: 'Fresh Recipes', content: getCompoundSentenceList },
      dry: { label: 'Baked Recipes', content: getCompoundSentenceList },
    };

    const count = index + 1;
    const backgroundColor = count % 2 ? 'hero.blueberry' : 'secondary.carrot';

    const startBoxSummaryList = getListItems(recipes, recipeFilters).filter(
      ({ content }) => content !== '',
    );
    if (fresh?.length > 0 && dry?.length > 0) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Mixed Bowl Plan',
      });
    } else if (fresh?.length > 0 && portionSize === HALF_PORTION) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Half Fresh Plan',
      });
    } else if (fresh?.length > 0) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Full Fresh Plan',
      });
    } else if (dry?.length > 0) {
      startBoxSummaryList.unshift({
        label: 'Meal Plan',
        content: 'Full Baked Plan',
      });
    }
    starterBoxes.push(
      <SummaryItem
        key={`starterBox-${index}`}
        title={`${petName}'s ${pupSummaryTitle}`}
        breeds={breeds}
        backgroundColor={backgroundColor}
        summaryList={startBoxSummaryList}
        addons={addons}
        price={price}
        discountedPrice={discountedPrice}
        starterBox
      />,
    );
  }

  const planFreeItems = getPlanFreeItems(plans);
  if (
    planFreeItems.scooperCount > 0 &&
    freeItems?.filter(e => e.label === 'Scoop').length === 0
  ) {
    freeItems.push({ label: 'Scoop', quantity: planFreeItems.scooperCount });
  }
  if (
    planFreeItems.bowlCount > 0 &&
    freeItems?.filter(e => e.label === 'Bowl').length === 0
  ) {
    freeItems.push({ label: 'Bowl', quantity: planFreeItems.bowlCount });
  }
  if (
    planFreeItems.puptainerCount > 0 &&
    freeItems?.filter(e => e.label === 'Puptainer').length === 0
  ) {
    freeItems.push({
      label: 'Puptainer',
      quantity: planFreeItems.puptainerCount,
    });
  }

  return (
    <StyledConfirmationOrderSummary>
      <OrderSummaryTitle>{orderSummaryTitle}</OrderSummaryTitle>
      {starterBoxes}
      {freeItems?.map((freeItem, index) => (
        <Section
          columns={2}
          key={index}
          hideBorderBottom={!(index === freeItems.length - 1)}
          smallMarginPadding
          firstMarginTop={index === 0}
          lastPaddingBottom={index === freeItems.length - 1}
        >
          <SectionSubtitle>
            {`${freeItem.label} `}
            {freeItem.quantity
              ? `X${freeItem.quantity}`
              : plans.length > 1
              ? `X${plans.length}`
              : null}
          </SectionSubtitle>
          <SectionSubtitle value color="status.successPrimary">
            FREE
          </SectionSubtitle>
        </Section>
      ))}
      <Section columns={2} hideBorderBottom smallMarginPadding firstMarginTop>
        <SectionSubtitle>Subtotal:</SectionSubtitle>
        <SectionSubtitle value>{orderSubtotal}</SectionSubtitle>
      </Section>
      {!!orderTotalTax && (
        <Section columns={2} hideBorderBottom smallMarginPadding>
          <SectionSubtitle>Taxes:</SectionSubtitle>
          <SectionSubtitle value>{orderTotalTax}</SectionSubtitle>
        </Section>
      )}
      {pricingList && (
        <Section columns={2} hideBorderBottom smallMarginPadding>
          <SectionSubtitle
            heading
            color="status.successPrimary"
          >{`${pricingList.discountLabel} (${pricingList.discountText})`}</SectionSubtitle>
          <SectionSubtitle
            value
            color="status.successPrimary"
          >{`-${costFormatter(pricingList.discountAmount)}`}</SectionSubtitle>
        </Section>
      )}
      <Section columns={2} smallMarginPadding hideBorderBottom>
        <SectionTitle>Total</SectionTitle>
        <SectionTitle>{orderTotalCost}</SectionTitle>
      </Section>
      <ButtonWrapper>
        <LinkButton
          href={`/account/`}
          variant="secondary"
          horizontalPadding={10}
          verticalPadding={4}
          onClick={onClickAccount}
        >
          {'View full order details'}
        </LinkButton>
      </ButtonWrapper>
    </StyledConfirmationOrderSummary>
  );
};

SlimConfirmationOrderSummary.displayName = 'SlimConfirmationOrderSummary';
SlimConfirmationOrderSummary.propTypes = {
  orderSummaryTitle: PropTypes.string,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      petName: PropTypes.string.isRequired,
      breeds: PropTypes.arrayOf(PropTypes.string.isRequired),
      price: PropTypes.string.isRequired,
      discountedPrice: PropTypes.string,
      pricePerPeriod: PropTypes.string.isRequired,
      weeksOfFood: PropTypes.number.isRequired,
      deliveryNext: PropTypes.string.isRequired,
      recipes: PropTypes.shape({
        fresh: PropTypes.arrayOf(PropTypes.string),
        dry: PropTypes.arrayOf(PropTypes.string),
      }).isRequired,
      addons: PropTypes.array,
      portionSize: PropTypes.string,
    }),
  ).isRequired,
  orderTotalCost: PropTypes.string,
  orderSubtotal: PropTypes.string,
  orderTotalTax: PropTypes.string,
  pricingList: PropTypes.shape({
    discountLabel: PropTypes.string,
    discountText: PropTypes.string,
    discountAmount: PropTypes.string,
  }),
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      petId: PropTypes.string,
      petName: PropTypes.string,
      productType: PropTypes.string,
    }),
  ),
  freeItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      quantity: PropTypes.number,
    }),
  ),
  onClickAccount: PropTypes.func,
};
